import { SubmitFilterDataType } from "@saleor/changeOrderExport/components/OrderFilterSection/constant";
import { ViewEditMode } from "@saleor/changeOrderExport/views/ViewEditOrder/types";
import {
  DomesticOrderConfirmationFilterInput,
  SapListOfSaleOrderInput
} from "@saleor/graphql";

import IAppState, { AppError } from "./state";

export type AppStateReducerActionType =
  | "displayError"
  | "displayLoader"
  | "setExpandMenu"
  | "setFullScreen"
  | "setCustomerId"
  | "setOrderCount"
  | "setBodyPaddingRight"
  | "setTabMaterial"
  | "setViewEditMode"
  | "setFilterOrder"
  | "setFilterOrderConfirm"
  | "setFilterListSalesOrder"
  | "showSnackbarAlternatedMaterial"
  | "showSnackbarEditAlternatedMaterial"
  | "showPoUpload"
  | "setSearchIdExport"
  | "setTabSoldToExport";

export interface AppStateReducerAction {
  payload: Partial<{
    error: AppError["type"];
    errorId: AppError["id"];
    value: boolean;
    customerId: string;
    orderCount: number;
    bodyPaddingRight: number;
    tabMaterial: number;
    viewEditMode: ViewEditMode;
    filterOrder: SubmitFilterDataType;
    filterOrderConfirm: DomesticOrderConfirmationFilterInput;
    filterListSalesOrder: SapListOfSaleOrderInput;
    snackbarAlterMaterial: boolean;
    snackbarEditAlternatedMaterial: boolean;
    msg: string;
    showPoUpload?: boolean;
    searchIdExport?: string;
    tabSoldToExport?: number;
  }>;
  type: AppStateReducerActionType;
}

function displayError(
  prevState: IAppState,
  errorType: AppError["type"],
  errorId?: AppError["id"]
): IAppState {
  return {
    ...prevState,
    error: {
      id: errorId,
      type: errorType
    },
    loading: false
  };
}

function displayLoader(prevState: IAppState, value: boolean): IAppState {
  return {
    ...prevState,
    loading: value
  };
}

function setExpandMenu(prevState: IAppState, value: boolean): IAppState {
  return {
    ...prevState,
    expandMenu: value
  };
}

function setFullScreen(prevState: IAppState, value: boolean): IAppState {
  return {
    ...prevState,
    fullScreen: value
  };
}

function setOrderCount(prevState: IAppState, value: number): IAppState {
  return {
    ...prevState,
    orderCount: value
  };
}

function setCustomerId(prevState: IAppState, customerId: string): IAppState {
  return {
    ...prevState,
    customerId
  };
}

function setBodyPaddingRight(prevState: IAppState, value: number): IAppState {
  return {
    ...prevState,
    bodyPaddingRight: value
  };
}

function setTabMaterial(prevState: IAppState, value: number): IAppState {
  return {
    ...prevState,
    tabMaterial: value
  };
}

function setViewEditMode(prevState: IAppState, value: ViewEditMode): IAppState {
  return {
    ...prevState,
    viewEditMode: value
  };
}

function setSnackbar(
  prevState: IAppState,
  value: boolean,
  msg?: string
): IAppState {
  return {
    ...prevState,
    snackbarAlterMaterial: value,
    snackbarMsg: msg
  };
}

function setEditSnackbar(prevState: IAppState, value: boolean): IAppState {
  return {
    ...prevState,
    snackbarEditAlterMaterial: value
  };
}

function setFilterOrder(
  prevState: IAppState,
  value: SubmitFilterDataType
): IAppState {
  return {
    ...prevState,
    filterOrder: value
  };
}

function setFilterOrderConfirm(
  prevState: IAppState,
  value: DomesticOrderConfirmationFilterInput
): IAppState {
  return {
    ...prevState,
    filterOrderConfirm: value
  };
}

function setFilterListSalesOrder(
  prevState: IAppState,
  value: SapListOfSaleOrderInput
): IAppState {
  return {
    ...prevState,
    filterListSalesOrder: value
  };
}

function showPoUpload(prevState: IAppState, value: boolean): IAppState {
  return {
    ...prevState,
    showPoUpload: value
  };
}

function setSearchIdExport(prevState: IAppState, value: string): IAppState {
  return {
    ...prevState,
    searchIdExport: value
  };
}

function setTabSoldToExport(prevState: IAppState, value: number): IAppState {
  return {
    ...prevState,
    tabSoldToExport: value
  };
}

function reduceAppState(
  prevState: IAppState,
  action: AppStateReducerAction
): IAppState {
  switch (action.type) {
    case "displayError":
      return displayError(
        prevState,
        action.payload.error,
        action.payload.errorId
      );
    case "displayLoader":
      return displayLoader(prevState, action.payload.value);
    case "setExpandMenu":
      return setExpandMenu(prevState, action.payload.value);
    case "setFullScreen":
      return setFullScreen(prevState, action.payload.value);
    case "setCustomerId":
      return setCustomerId(prevState, action.payload.customerId);
    case "setOrderCount":
      return setOrderCount(prevState, action.payload.orderCount);
    case "setBodyPaddingRight":
      return setBodyPaddingRight(prevState, action.payload.bodyPaddingRight);
    case "setTabMaterial":
      return setTabMaterial(prevState, action.payload.tabMaterial);
    case "setViewEditMode":
      return setViewEditMode(prevState, action.payload.viewEditMode);
    case "setFilterOrder":
      return setFilterOrder(prevState, action.payload.filterOrder);
    case "setFilterOrderConfirm":
      return setFilterOrderConfirm(
        prevState,
        action.payload.filterOrderConfirm
      );
    case "setFilterListSalesOrder":
      return setFilterListSalesOrder(
        prevState,
        action.payload.filterListSalesOrder
      );
    case "showSnackbarAlternatedMaterial":
      return setSnackbar(prevState, action.payload.snackbarAlterMaterial);
    case "showSnackbarEditAlternatedMaterial":
      return setEditSnackbar(
        prevState,
        action.payload.snackbarEditAlternatedMaterial
      );
    case "showPoUpload":
      return showPoUpload(prevState, action.payload.showPoUpload);
    case "setSearchIdExport":
      return setSearchIdExport(prevState, action.payload.searchIdExport);
    case "setTabSoldToExport":
      return setTabSoldToExport(prevState, action.payload.tabSoldToExport);
    default:
      return prevState;
  }
}

export default reduceAppState;
